/**
 * ReservationsDashboardWidget — Iter 310
 *
 * Live-Übersicht der Chatbot-Slot-Reservierungen für das Admin-Dashboard.
 * Zeigt:
 *   - aktive offene Reservierungen
 *   - heute bestätigt
 *   - heute abgelaufen
 *   - 7-Tage-Conversion-Rate
 *   - Top 3 "bald ablaufende" Reservierungen mit Link zum Kalender
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Timer, Check, X, TrendingUp, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRemaining = (expiresAt, now) => {
  const remaining = new Date(expiresAt).getTime() - now;
  if (remaining <= 0) return 'abgelaufen';
  const min = Math.floor(remaining / 60000);
  if (min >= 60) return `${Math.floor(min / 60)}h ${min % 60}min`;
  return `${min}min`;
};

const ReservationsDashboardWidget = () => {
  const [stats, setStats] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/reservations/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (e) {
      // Stiller Fehler — Widget bleibt verborgen
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const tick = setInterval(() => setNow(Date.now()), 30000);
    const refresh = setInterval(fetchStats, 60000);
    return () => { clearInterval(tick); clearInterval(refresh); };
  }, [fetchStats]);

  if (!stats) return null;
  // Widget komplett verstecken wenn keinerlei Aktivität (saubere Dashboard)
  if (
    stats.active === 0 &&
    stats.confirmed_today === 0 &&
    stats.expired_today === 0 &&
    stats.confirmed_7d === 0 &&
    stats.expired_7d === 0
  ) {
    return null;
  }

  return (
    <Card
      className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50"
      data-testid="reservations-dashboard-widget"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Timer className="w-5 h-5 text-amber-600" />
          Chatbot-Reservierungen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            icon={<Timer className="w-4 h-4 text-amber-600" />}
            label="Aktiv"
            value={stats.active}
            color="text-amber-700"
            testId="stat-active"
          />
          <Stat
            icon={<Check className="w-4 h-4 text-emerald-600" />}
            label="Heute bestätigt"
            value={stats.confirmed_today}
            color="text-emerald-700"
            testId="stat-confirmed-today"
          />
          <Stat
            icon={<X className="w-4 h-4 text-red-500" />}
            label="Heute abgelaufen"
            value={stats.expired_today}
            color="text-red-600"
            testId="stat-expired-today"
          />
          <Stat
            icon={<TrendingUp className="w-4 h-4 text-blue-600" />}
            label="Conversion (7T)"
            value={stats.conversion_rate_7d !== null ? `${stats.conversion_rate_7d}%` : '—'}
            color="text-blue-700"
            testId="stat-conversion"
          />
        </div>

        {stats.soon_to_expire?.length > 0 && (
          <div className="border-t border-amber-200/60 pt-3">
            <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Läuft demnächst ab
            </div>
            <div className="space-y-2">
              {stats.soon_to_expire.map((r) => {
                const remaining = new Date(r.expires_at).getTime() - now;
                const isUrgent = remaining < 30 * 60 * 1000;
                return (
                  <div
                    key={r.id}
                    data-testid={`soon-expire-${r.id}`}
                    className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 text-sm"
                  >
                    <div className="flex flex-col">
                      <span className="text-gray-800 font-medium">
                        {new Date(r.start).toLocaleString('de-CH', {
                          weekday: 'short', day: '2-digit', month: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                      <span className="text-xs text-gray-500">
                        Reservation {r.id.slice(0, 8)}
                      </span>
                    </div>
                    <div className={`text-xs font-bold px-2 py-1 rounded-full ${
                      isUrgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      🔒 {formatRemaining(r.expires_at, now)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {stats.confirmed_7d + stats.expired_7d >= 5 && (
          <div className="text-xs text-amber-900/80 bg-white/50 rounded-lg px-3 py-2">
            <strong>Letzte 7 Tage:</strong> {stats.confirmed_7d} Termine bestätigt,
            {' '}{stats.expired_7d} abgelaufen.
            {stats.conversion_rate_7d !== null && (
              <> Deine Reschedule-Quote liegt bei <strong>{stats.conversion_rate_7d}%</strong>.</>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ icon, label, value, color, testId }) => (
  <div className="bg-white/60 rounded-lg p-3" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-xs text-gray-600">
      {icon}
      {label}
    </div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </div>
);

export default ReservationsDashboardWidget;
